import * as Sentry from '@sentry/react';
import env from '@frontend/env';

/*This is a list of regular expressions matching the error messages we wish to ignore from sentry exceptions capturing.
Please add here all error messages that do not affect the normal operation of the app.*/
const ignorePatterns: (RegExp | string)[] = [
  /status code 40[134]/gim,
  /The Stripe Terminal ConnectionToken has already been redeemed./gi,
  /Cannot find a descendant at path/gim,
  /Cannot resolve a DOM point from Slate point/gim,
  /Location-Id not configured/gi,
  /Cannot get the leaf node at paths/gi,
  /invalid \| token is expired by \d+h\d+m\d+s/gi,
  /expired \| token is expired by \d+h\d+m\d+s/gi,
  'AbortError: The play() request was interrupted by end of playback.',
  'AbortError: The play() request was interrupted by a new load request.',
  "NotAllowedError: play() failed because the user didn't interact with the document first.",
  'Non-Error exception captured',
  'Non-Error promise rejection captured',
  'Event `Event` (type=error) captured as promise rejection',
  'onDrag called before onDragStart.',
  'onDragEnd called before onDragStart.',
  /Cannot assign to read only property 'x' of object.*/,
  /FirebaseError: Missing or insufficient permissions.*/,
  /FirebaseError: The datastore operation timed out, or the data was temporarily unavailable.*/,
];

/* This is an array of regular expressions matching the endpoints we wish to ignore.
If any endpoint returns errors that do not affect the application's operation, you should add it here. */
const ignoreEndpoints: RegExp[] = [/person_extended/i, /stripe\.com/i, /pendo\.io/i];
type Environment = 'test' | 'preview' | 'production' | 'debug';

/**
 * We initialize Sentry with the DSN (which is the URL of the Sentry server) and the release (which is
 * the version of the app that is running)
 */
export const getSentryConfig = (): Partial<Sentry.BrowserOptions> => {
  // Set the Sentry environment based on the Vite environment variables
  let environment: Environment = 'test';
  if (env.IS_PREVIEW === 'true') {
    environment = 'preview';
  } else if (import.meta.env.MODE === 'production') {
    environment = 'production';
  }
  // environment = 'debug'; // Force debug environment for sentry development

  const DEFAULT_SAMPLE_RATE = environment === 'production' ? 0.25 : 0;

  return {
    dsn: env.SENTRY_DSN,

    // IMPORTANT: This has to match what we have for our source maps in reusable-build-and-deploy.yml
    release: env.VERSION,
    environment,
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 0.25,

    // Note: error sampling is handled in beforeSend to support per-event overrides. sampleRate is not set here to avoid double-sampling.

    //Performance sample rate
    tracesSampleRate: environment === 'production' ? 0.01 : 0,
    attachStacktrace: true,

    denyUrls: ignoreEndpoints,
    ignoreErrors: ignorePatterns,
    ignoreTransactions: ignorePatterns,
    transport: Sentry.makeBrowserOfflineTransport(Sentry.makeFetchTransport),
    transportOptions: {
      //@ts-ignore
      maxQueueSize: 10,
    },
    integrations: [
      Sentry.browserTracingIntegration({ instrumentNavigation: true, enableInp: true }),
      Sentry.replayIntegration({
        stickySession: true,
      }),
    ],
    beforeSendSpan(span) {
      // filter out noisy pendo requests
      if (span?.op === 'resource.script' && span?.description?.includes('pendo-static-')) {
        // any type because Sentry types have a type error -- it explicitly says to return null to drop a span
        return null as any; // Ignore spans related to Pendo static resources
      }
      return span;
    },
    beforeSend(event: Sentry.ErrorEvent, _hint: Sentry.EventHint) {
      // the event can override the default sample rate in production, otherwise it falls back to the default
      const eventSampleRate =
        environment === 'production' && typeof event?.contexts?.meta?.sampleRate === 'number'
          ? event.contexts.meta.sampleRate
          : undefined;
      const sampleRate = eventSampleRate ?? DEFAULT_SAMPLE_RATE;
      return Math.random() < sampleRate ? event : null;
    },
  };
};
